/**
 * @file tabs 组件的 tab bar 组件
 */
import {
    useMemo, FC, ReactElement, ReactNode, CSSProperties, Key, LegacyRef, useEffect, useRef, useCallback,
} from 'react';
import classnames from 'classnames';
import {TopicModule} from '@/services/pages/channel/channel.client';
import {logClkReport} from '@/utils/ubcUtils';
import Exposure from '@/utils/exposure';
import {VALUE_TYPE} from '@/dicts/consts';
import styles from './index.less';

const prefixCls = 'lemon-tabs';

interface TabBarProps {
    className?: string;
    style?: CSSProperties;
    tabStyle?: CSSProperties;
    forwardRef?: LegacyRef<HTMLDivElement>;
    activeKey: Key | null;
    tabBarExtraContent?: ReactNode;
    panes: ReactElement[];
    onChange?: (index: number) => void;
    isFilter?: boolean;
    page?: string;
    sfRef?: string;
    infos?: TopicModule;
    channelBusinessLine?: string;
 }

const TabBar: FC<TabBarProps> = ({
    className,
    style,
    tabStyle,
    forwardRef,
    activeKey,
    tabBarExtraContent,
    panes,
    onChange,
    page = '',
    infos,
    channelBusinessLine = '',
}) => {
    const scrolBar = useRef<HTMLDivElement>(null);
    const avtiveLine = useRef<HTMLDivElement>(null);
    const tabs = useMemo<ReactElement[]>(
        () => panes.filter(pane => typeof pane.props.tabSpan !== 'number' || pane.props.tabSpan >= 1),
        [panes]
    );
    useEffect(
        () => {
            if (avtiveLine.current && scrolBar.current) {
                const rect = avtiveLine.current.getBoundingClientRect();
                const screenWidth = document.documentElement.clientWidth;
                // eslint-disable-next-line max-len
                scrolBar.current.scrollLeft = rect.left - screenWidth / 2 + rect.width / 2 + scrolBar.current.scrollLeft;
            }
        },
        [activeKey]
    );

    const activeTabKey = useMemo(
        () => {
            // eslint-disable-next-line @typescript-eslint/init-declarations
            let activePane;
            for (let i = panes.length - 1; i >= 0; i--) {
                const pane = panes[i];
                const tabSpan = pane.props.tabSpan ?? 1;
                if (pane.key === String(activeKey)) {
                    activePane = pane;
                }
                if (activePane && tabSpan > 0) {
                    return pane.key;
                }
            }

            return tabs[0]?.key;
        },
        [tabs, panes, activeKey]
    );

    const handleTabChange = useCallback(
        (tabIndex: number, extInfo) => {
            const paneIndex = panes.indexOf(tabs[tabIndex]);
            onChange?.(paneIndex);
            setTimeout(() => {
                Exposure.instance?.collectElements();
            }, 0);
            logClkReport(
                page,
                'rongqi_tab',
                infos?.channelId,
                channelBusinessLine,
                extInfo?.moduleId,
                extInfo?.moduleName,
                {
                    // eslint-disable-next-line camelcase
                    value_type: VALUE_TYPE.MODULE,
                    // eslint-disable-next-line camelcase
                    value_id: extInfo?.moduleId,
                    // eslint-disable-next-line camelcase
                    value_title: extInfo?.moduleName,
                }
            );
        },
        [channelBusinessLine, infos?.channelId, onChange, page, panes, tabs]
    );


    return (
        <div
            className={classnames(
                styles.nav,
                `${prefixCls}-nav`,
                className
            )}
            style={style}
            ref={scrolBar}
            data-show={
                JSON.stringify({
                    'page': page,
                    'value': 'rongqi_tab',
                    'pageSourceId': infos?.channelId,
                    'pageType': channelBusinessLine,
                    'valueId': infos?.moduleId,
                    'valueTitle': infos?.moduleName,
                    'valueType': VALUE_TYPE.MODULE,
                })
            }
        >
            {tabs.map((pane: any, tabIndex: any) => {
                const key = pane.key;
                const {tab, extInfo} = pane.props;
                const active = key === activeTabKey;

                return (
                    <div
                        key={key ?? undefined}
                        ref={forwardRef}
                        className={classnames(
                            styles.tab,
                            `${prefixCls}-tab`,
                            {
                                [styles.active]: active,
                                [`${prefixCls}-active`]: active,
                            }
                        )}
                        style={active ? tabStyle : {}}
                        onClick={() => handleTabChange(tabIndex, extInfo)}
                        data-show={
                            JSON.stringify({
                                'page': page,
                                'value': 'rongqi_tab',
                                'pageSourceId': infos?.channelId,
                                'pageType': channelBusinessLine,
                                'valueId': extInfo?.moduleId,
                                'valueTitle': extInfo?.moduleName,
                                'valueType': VALUE_TYPE.MODULE,
                            })
                        }
                    >
                        <div className={styles.titleWrap}>
                            <div className={classnames(styles.tabTitle, {[styles.activeT]: active})}>
                                {tab[0]}
                            </div>
                            {
                                (tab[1]) && (
                                    <div className={classnames(styles.tabSubTitle, {[styles.activeT]: active})}>
                                        {tab[1]}
                                    </div>
                                )
                            }
                        </div>
                        {active
                            ? (
                                <div
                                    id="underline"
                                    className={classnames(styles.underline, `${prefixCls}-underline`)}
                                    ref={avtiveLine}
                                />
                            )
                            : null
                        }
                    </div>
                );
            })}
            {tabBarExtraContent}
        </div>
    );
};

export default TabBar;

